// src/components/Navbar.jsx
import * as React from 'react'
import { Link as RouterLink, NavLink, useLocation, useNavigate } from 'react-router-dom'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Container from '@mui/material/Container'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import MenuIcon from '@mui/icons-material/Menu'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import Badge from '@mui/material/Badge'
import useMediaQuery from '@mui/material/useMediaQuery'

import logo from '../assets/logo/123back.png'
import frFlag from '../assets/icones/fr-flag.png'
import ukFlag from '../assets/icones/uk-flag.png'
import plFlag from '../assets/icones/pl-flag.png'
import esFlag from '../assets/icones/es-flag.png'
import itFlag from '../assets/icones/it-flag.svg'
import deFlag from '../assets/icones/ge-flag.svg'
import ptFlag from '../assets/icones/po.jpg'
import csFlag from '../assets/icones/rp-flag.png'
import nlFlag from '../assets/icones/pb-flag.png'

// auth
import { getUser, isLoggedIn } from '../lib/auth'

// i18n
import { useI18n } from '../i18n/I18nProvider'
import navbarI18n from '../i18n/components/navbar.i18n'

const LANGS = [
  { code: 'pl', flag: plFlag, alt: 'Polski' },
  { code: 'en', flag: ukFlag, alt: 'English' },
  { code: 'fr', flag: frFlag, alt: 'Français' },
  { code: 'es', flag: esFlag, alt: 'Español' },
  { code: 'it', flag: itFlag, alt: 'Italiano' },
  { code: 'de', flag: deFlag, alt: 'Deutsch' },
  { code: 'pt', flag: ptFlag, alt: 'Português' },
  { code: 'cs', flag: csFlag, alt: 'Čeština' },
  { code: 'nl', flag: nlFlag, alt: 'Nederlands' },
]

export default function Navbar() {
  const [open, setOpen] = React.useState(false)
  const [anchorEl, setAnchorEl] = React.useState(null)
  const isBurger = useMediaQuery('(max-width:1016px)')

  const { t, lang, setLang, register } = useI18n()
  React.useEffect(() => { register('navbar', navbarI18n) }, [register])

  const location = useLocation()
  const nav = useNavigate()
  const isActive = (to) => location.pathname === to
  const current = LANGS.find(l => l.code === lang) || LANGS[0]

  const links = React.useMemo(() => ([
    { to: '/tarifs',          label: t('navbar.links.tarifs') },
    { to: '/criteres-rachat', label: t('navbar.links.criteria') },
    { to: '/envoi-securise',  label: t('navbar.links.secureSend') },
  ]), [t])

  const accountActive = React.useMemo(() => {
    const p = location.pathname || ''
    return p.startsWith('/admin') || p.startsWith('/mes-envois')
  }, [location.pathname])

  const goToAccount = () => {
    if (!isLoggedIn()) {
      try {
        const payload = { redirectTo: location.pathname }
        sessionStorage.setItem('redirectAfterAuth', JSON.stringify(payload))
      } catch {}
      const opener = typeof window !== 'undefined' ? window.openAuth : undefined
      if (typeof opener === 'function') opener('login', { redirectTo: location.pathname })
      else nav('/login', { state: { redirectTo: location.pathname } })
      return
    }
    const u = getUser()
    if (u?.role === 'admin' || u?.role === 'superadmin') return nav('/admin')
    return nav('/mes-envois')
  }

  // ------- Badge panier -------
  const [cartCount, setCartCount] = React.useState(0)
  React.useEffect(() => {
    const read = () => {
      try {
        const raw = localStorage.getItem('cart.meta')
        const meta = raw ? JSON.parse(raw) : { totalQty: 0 }
        setCartCount(Number(meta.totalQty || 0))
      } catch { setCartCount(0) }
    }
    read()
    const onUpdate = () => read()
    window.addEventListener('cart:update', onUpdate)
    window.addEventListener('storage', onUpdate)
    return () => {
      window.removeEventListener('cart:update', onUpdate)
      window.removeEventListener('storage', onUpdate)
    }
  }, [])

  const goToCart = () => nav('/recap')

  return (
    <>
      <AppBar position="sticky" elevation={0} sx={{ backgroundColor: '#fff', borderBottom: '1px solid #eee' }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: 72, display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Logo */}
            <Box component={RouterLink} to="/services" sx={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', mr: 2 }}>
              <Box component="img" src={logo} alt="123back" sx={{ height: 25, width: 'auto' }} />
            </Box>

            {/* Liens centraux */}
            <Box sx={{ display: isBurger ? 'none' : 'flex', gap: 4, flexGrow: 1, justifyContent: 'center' }}>
              {links.map((l) => {
                const active = isActive(l.to)
                const isSellNow = l.to === '/tarifs'
                return isSellNow ? (
                  <Button
                    key={l.to}
                    component={NavLink}
                    to={l.to}
                    disableRipple
                    variant="contained"
                    aria-label={l.label}
                    sx={{
                      px: 2.2, py: 0.8, borderRadius: 9999, fontSize: '1rem', fontWeight: 800, textTransform: 'none',
                      backgroundColor: '#0466c8', color: '#fff',
                      boxShadow: '0 4px 10px rgba(4,102,200,0.18)',
                      transform: active ? 'translateY(-1px)' : 'none',
                      transition: 'transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
                      '&:hover': { backgroundColor: '#0358ab', boxShadow: '0 6px 14px rgba(4,102,200,0.22)' },
                      '&.active': { backgroundColor: '#0358ab' },
                    }}
                  >
                    {l.label}
                  </Button>
                ) : (
                  <Button
                    key={l.to}
                    component={NavLink}
                    to={l.to}
                    disableRipple
                    sx={{
                      px: 0, minWidth: 'auto', color: 'text.primary', fontSize: '1rem',
                      fontWeight: active ? 700 : 500, textTransform: 'none',
                      '&:hover': { background: 'transparent', opacity: 0.85 },
                    }}
                  >
                    {l.label}
                  </Button>
                )
              })}
            </Box>

            {/* Zone droite : Panier + Compte + Langue */}
            <Box sx={{ display: isBurger ? 'none' : 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* Panier */}
              <IconButton onClick={goToCart} aria-label={t('navbar.cart')} sx={{ mr: 0.5 }}>
                <Badge
                  badgeContent={cartCount}
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#0466c8',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      minWidth: 22,
                      height: 22,
                      border: '2px solid #fff',
                      boxShadow: '0 0 4px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <ShoppingCartOutlinedIcon />
                </Badge>
              </IconButton>

              {/* Mes envois */}
              <Button
                onClick={goToAccount}
                disableRipple
                sx={{
                  px: 0, minWidth: 'auto', color: 'text.primary', fontSize: '1rem',
                  textTransform: 'none', fontWeight: accountActive ? 700 : 500,
                  '&:hover': { background: 'transparent', opacity: 0.85 },
                }}
              >
                {t('navbar.account')}
              </Button>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              {/* Langue */}
              <IconButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                size="small"
                sx={{ p: 0.5, borderRadius: 2, border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <Box component="img" src={current.flag} alt={current.alt} sx={{ height: 18, width: 'auto' }} />
                <ArrowDropDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {LANGS.map((l) => (
                  <MenuItem
                    key={l.code}
                    onClick={() => { setLang(l.code); setAnchorEl(null) }}
                    selected={l.code === lang}
                    sx={{ minWidth: 48, justifyContent: 'center', py: 0.5 }}
                  >
                    <Box component="img" src={l.flag} alt={l.alt} sx={{ height: 18, width: 'auto' }} />
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            {/* Burger mobile */}
            <Box sx={{ flexGrow: 1, display: isBurger ? 'block' : 'none' }} />
            <IconButton
              onClick={() => setOpen(true)}
              sx={{ display: isBurger ? 'inline-flex' : 'none' }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Drawer mobile */}
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{ width: 300, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 4, px: 3 }}
          role="presentation"
          onClick={() => setOpen(false)}
        >
          {/* 🌍 Langues */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 3 }}>
            {LANGS.map((l) => (
              <IconButton
                key={l.code}
                onClick={() => setLang(l.code)}
                size="small"
                sx={{
                  p: 0.4, borderRadius: 2, opacity: lang === l.code ? 1 : 0.6,
                  border: lang === l.code ? '1px solid #e0e0e0' : '1px solid transparent',
                }}
              >
                <Box component="img" src={l.flag} alt={l.alt} sx={{ height: 20, width: 'auto' }} />
              </IconButton>
            ))}
          </Box>

          <Divider sx={{ width: '70%', mb: 3 }} />

          <List sx={{ width: '100%' }}>
            {links.map((l) => {
              const active = isActive(l.to)
              const isSellNow = l.to === '/tarifs'
              if (isSellNow) {
                return (
                  <ListItem key={l.to} disablePadding sx={{ mb: 2 }}>
                    <Button
                      component={RouterLink}
                      to={l.to}
                      fullWidth
                      variant="contained"
                      disableRipple
                      sx={{
                        borderRadius: 3, py: 1.2, px: 2, fontWeight: 800,
                        textTransform: 'none', backgroundColor: '#0466c8', color: '#fff',
                        boxShadow: 'none', '&:hover': { backgroundColor: '#0358ab', boxShadow: 'none' },
                      }}
                    >
                      {l.label}
                    </Button>
                  </ListItem>
                )
              }
              return (
                <ListItem key={l.to} disablePadding>
                  <ListItemButton component={RouterLink} to={l.to} selected={active} sx={{ justifyContent: 'center' }}>
                    <ListItemText
                      primaryTypographyProps={{ fontSize: '1rem', fontWeight: active ? 700 : 500, textAlign: 'center' }}
                      primary={l.label}
                    />
                  </ListItemButton>
                </ListItem>
              )
            })}

            {/* Panier mobile */}
            <ListItem disablePadding>
              <ListItemButton component={RouterLink} to="/recap" sx={{ justifyContent: 'center' }}>
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1rem', fontWeight: 500, textAlign: 'center' }}
                  primary={t('navbar.cart', 'Panier')}
                />
              </ListItemButton>
            </ListItem>

            {/* Mes envois */}
            <ListItem disablePadding sx={{ mt: 0.5 }}>
              <ListItemButton onClick={goToAccount} selected={accountActive} sx={{ justifyContent: 'center' }}>
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1rem', fontWeight: accountActive ? 700 : 500, textAlign: 'center' }}
                  primary={t('navbar.account')}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </>
  )
}
